// Copied from platform-app components/pos/PosThemeTab.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useState, useEffect } from 'react';
import { Save, Moon, Sun } from 'lucide-react';
import { PREVIEW_PALETTE, type PreviewTheme } from './layout/previewPalette';
import { resolveAddKeyLook } from './layout/addKeyLook';

import { RubyGemMark } from '../RubyLoader';
/** Default ADD key look (no merchant colours) — theme-independent. */
const ADD_LOOK = resolveAddKeyLook(undefined, 'light');

export type PosThemeValue = 'light' | 'dark';
export type PosThemeScope = 'terminal' | 'all';

interface Props {
  /** Current saved theme for this terminal (defaults to 'light'). */
  initialTheme?: PosThemeValue;
  /** Whether "all terminals at this location" is offered. */
  otherTerminalsExist?: boolean;
  onSave: (theme: PosThemeValue, scope: PosThemeScope) => Promise<void>;
  saving?: boolean;
  /**
   * 'terminal' — the tab is editing one till, so it offers a publish scope and
   * the save reaches the device immediately.
   * 'template' — there is no till yet; the theme is stored on the layout
   * template and lands on terminals when the template is published.
   */
  variant?: 'terminal' | 'template';
}

/**
 * POS Theme sub-tab — pick the cashier-side theme (Light / Dark) and publish it
 * to this terminal or all terminals at the location. Mirrors the button-layout
 * publish flow: the change lands on the register config and is pushed live via
 * the config:pos-theme WebSocket event (no device reload).
 *
 * The preview is a faithful miniature of the sale screen driven by the same
 * token values the POS uses, so what the admin picks is what the cashier sees.
 */
export default function PosThemeTab({ initialTheme = 'light', otherTerminalsExist, onSave, saving, variant = 'terminal' }: Props) {
  const [theme, setTheme] = useState<PosThemeValue>(initialTheme);
  const [scope, setScope] = useState<PosThemeScope>('terminal');
  const dirty = theme !== initialTheme;
  const isTemplate = variant === 'template';

  // Follow the saved value when it arrives/changes after mount (e.g. the
  // template loads, or a Save Layout round-trip refreshes it).
  useEffect(() => { setTheme(initialTheme); }, [initialTheme]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5 space-y-5">
      <div>
        <h3 className="text-stone-800 text-sm font-semibold">Cashier Theme</h3>
        <p className="text-stone-500 text-xs mt-0.5">
          Choose the look of the cashier (register) side. The customer-facing display has its own theme.
          {/* POS Defaults: wording — the default is never published, new tills start on it. */}
          {isTemplate && ' Saved on the RubyTill default — new tills of a business without its own layout start on this theme.'}
        </p>
      </div>

      {/* Theme choice + live preview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ThemeCard label="Light" sub="Default" Icon={Sun} value="light" active={theme === 'light'} onSelect={setTheme} />
        <ThemeCard label="Dark" Icon={Moon} value="dark" active={theme === 'dark'} onSelect={setTheme} />
      </div>

      {/* Publish scope — a template has no terminal of its own to scope to. */}
      <div className={isTemplate ? 'hidden' : undefined}>
        <div className="text-xs font-semibold text-stone-600 mb-2">Apply to</div>
        <div className="flex flex-wrap gap-2">
          <ScopeChip label="This terminal" value="terminal" active={scope === 'terminal'} onSelect={setScope} />
          {otherTerminalsExist && (
            <ScopeChip label="All terminals at this location" value="all" active={scope === 'all'} onSelect={setScope} />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-stone-500">
          {dirty
            ? 'Unsaved theme change.'
            : isTemplate
              ? 'Theme saved on the RubyTill default.'
              : 'Theme matches the saved value.'}
        </span>
        <button
          type="button"
          onClick={() => onSave(theme, scope)}
          disabled={!!saving || (!dirty && scope === 'terminal')}
          className="flex items-center gap-2 px-5 py-2 rounded-lg bg-accent-600 text-white text-sm font-semibold hover:bg-accent-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />}
          {scope === 'all' ? 'Publish to all' : 'Save Theme'}
        </button>
      </div>
    </div>
  );
}

function ThemeCard({ label, sub, Icon, value, active, onSelect }: {
  label: string; sub?: string; Icon: typeof Moon; value: PosThemeValue;
  active: boolean; onSelect: (v: PosThemeValue) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(value)}
      className={`text-left rounded-xl border-2 p-3 transition-all ${
        active ? 'border-accent-500 ring-2 ring-accent-500/20' : 'border-stone-200 hover:border-stone-300'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={active ? 'text-accent-600' : 'text-stone-500'} />
        <span className="text-sm font-bold text-stone-800">{label}</span>
        {sub && <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">{sub}</span>}
      </div>
      <SalePreview theme={value} />
    </button>
  );
}

function ScopeChip({ label, value, active, onSelect }: {
  label: string; value: PosThemeScope; active: boolean; onSelect: (v: PosThemeScope) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
        active ? 'bg-accent-600 text-white border-accent-600' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * Miniature sale screen — a mini side-rail layout using the exact preview
 * palette (`components/pos/layout/previewPalette.ts`) the till mirrors:
 * a rail column, a white cart, a keypad with an ink ADD key, and a summary
 * strip with the ruby PAY tile. See `RailPreview.tsx` for the full-size version.
 */
function SalePreview({ theme }: { theme: PreviewTheme }) {
  const p = PREVIEW_PALETTE[theme];
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${p.railBor}` }}>
      <div style={{ display: 'flex', height: 46 }}>
        {/* rail */}
        <div style={{ width: 18, background: p.railBg, borderRight: `1px solid ${p.railBor}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, paddingTop: 4 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: p.key, border: `1px solid ${p.keyBor}` }} />
          <div style={{ width: 12, height: 12, borderRadius: 3, background: p.key, border: `1px solid ${p.keyBor}` }} />
        </div>
        {/* cart (always white) */}
        <div style={{ flex: 1, background: '#ffffff', padding: 4 }}>
          <div style={{ height: 6, width: '80%', background: '#e5e7eb', borderRadius: 2, marginBottom: 3 }} />
          <div style={{ height: 6, width: '55%', background: '#eef0f2', borderRadius: 2 }} />
        </div>
        {/* keypad, green ADD key (Custom Item default — same in Light and Dark) */}
        <div style={{ width: '40%', background: p.kpBg, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2, padding: 3 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} style={{ background: p.key, border: `1px solid ${p.keyBor}`, color: p.keyTx, fontSize: 6, textAlign: 'center', borderRadius: 2, padding: '2px 0' }}>{n}</div>
          ))}
          <div style={{ background: ADD_LOOK.style.background, color: ADD_LOOK.fg, fontSize: 6, fontWeight: 800, textAlign: 'center', borderRadius: 2, padding: '2px 0' }}>ADD</div>
        </div>
      </div>
      {/* summary + ruby PAY */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 4, background: p.head, borderTop: `1px solid ${p.railBor}` }}>
        <div style={{ flex: 1 }} />
        <div style={{ background: '#DC143C', color: '#fff', fontSize: 7, fontWeight: 800, borderRadius: 3, padding: '3px 10px' }}>PAY</div>
      </div>
    </div>
  );
}
